import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import { blueGrey } from "@mui/material/colors";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  createTheme,
  responsiveFontSizes,
  ThemeProvider,
} from "@mui/material/styles";

import Abstract from "./components/Abstract";
import Demo from "./components/Demo";
import Approach from "./components/Approach";
import ImageAttributions from "./components/ImageAttributions";

const font_theme = responsiveFontSizes(
  createTheme({
    typography: {
      allVariants: {
        fontFamily: ["Google Sans", "sans-serif", "Roboto"].join(","),
      },
    },
  })
);

const mainTheme = {
  headerColor: blueGrey.A700,
  headerButtonColor: "white_button",
  headerTextColor: "white",
  firstColor: "#F3F6F9",
  secondColor: "#FFFFFF",
};

// ── BibTeX entries ──────────────────────────────────────────────────────────
const bibtexEntries = [
  {
    label: "VisualRAG (SoICT 2025)",
    code: `@inproceedings{Tran2025VisualRAG,
  title     = {Visual Retrieval-Augmented Generation for Silhouette-Guided Animal Art},
  author    = {Tran, Quoc-Duy and Vo, Anh-Tuan and Le, Trung-Nghia},
  booktitle = {International Symposium on Information and Communication Technology (SoICT)},
  year      = {2025},
  url       = {https://arxiv.org/abs/2606.17431},
  project_page = {https://seeme-rag.github.io/}
}`,
  },
  {
    label: "SEEME (Pattern Recognition Letters 2026)",
    code: `@article{Tran2026PRL,
  title        = {You Can See Me: Retrieval-Augmented Framework for Divergent
                  Perception in Animal Art Generation},
  author       = {Tran, Quoc-Duy and Vo, Anh-Tuan and Nguyen, Tam V. and
                  Tran, Minh-Triet and Le, Trung-Nghia},
  journal      = {Pattern Recognition Letters},
  year         = {2026},
  project_page = {https://seeme-rag.github.io/}
}`,
  },
];

// ── Publication list ────────────────────────────────────────────────────────
const publications = [
  {
    authors: "Quoc-Duy Tran, Anh-Tuan Vo, Trung-Nghia Le",
    title: "Visual Retrieval-Augmented Generation for Silhouette-Guided Animal Art",
    venue: "International Symposium on Information and Communication Technology (SoICT), 2025",
    arxiv: "https://arxiv.org/abs/2606.17431",
  },
  {
    authors:
      "Quoc-Duy Tran, Anh-Tuan Vo, Tam V. Nguyen, Minh-Triet Tran, Trung-Nghia Le",
    title:
      "You Can See Me: Retrieval-Augmented Framework for Divergent Perception in Animal Art Generation",
    venue: "Pattern Recognition Letters (PRL), 2026",
    arxiv: null, // Soon
  },
];

export default function App() {
  const [open, setOpen] = React.useState(false);

  const queryTheme = useTheme();
  const isSmall = useMediaQuery(queryTheme.breakpoints.down("sm"));

  const handleCloseAtt = () => {
    setOpen(false);
  };

  const nameSpacing = isSmall ? "   " : "        ";

  return (
    <React.Fragment>
      <div style={{ width: "100%", zIndex: 1, position: null }}>
        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <Box
          style={{
            backgroundColor: mainTheme.headerColor,
            display: "flex",
            flexDirection: "column",
            color: mainTheme.headerTextColor,
            textAlign: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Container maxWidth="lg">
            <ThemeProvider theme={font_theme}>
              <Typography
                variant={isSmall ? "h4" : "h3"}
                sx={{ my: 4, mt: 8, fontWeight: 500, lineHeight: 1.125 }}
              >
                You Can See Me: Retrieval-Augmented Framework for Divergent
                Perception in Animal Art Generation
              </Typography>
            </ThemeProvider>

            <Box style={{ marginBottom: 40, width: "100%" }}>
              <ThemeProvider theme={font_theme}>
                <Typography variant="h6" sx={{ mb: 4 }}>
                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 2,
                      fontWeight: 400,
                      fontSize: isSmall ? 14 : null,
                    }}
                  >
                    Quoc-Duy Tran<sup>1, 2</sup>{nameSpacing}
                    Anh-Tuan Vo<sup>1, 2</sup>{nameSpacing}
                    Tam V. Nguyen<sup>3</sup>{nameSpacing}
                    Minh-Triet Tran<sup>1, 2</sup>{nameSpacing}
                    Trung-Nghia Le<sup>1, 2</sup>
                  </p>
                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 2,
                      fontWeight: isSmall ? 400 : 600,
                    }}
                  >
                    <sup>1</sup>University of Science, Ho Chi Minh City, Viet Nam
                    <br />
                    <sup>2</sup>Vietnam National University, Ho Chi Minh City, Viet Nam
                    <br />
                    <sup>3</sup>University of Dayton, Ohio, United States
                  </p>
                </Typography>
              </ThemeProvider>

              <Container
                sx={{
                  "& a": { m: 1 },
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Button
                  href="https://arxiv.org/abs/2606.17431"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="large"
                  color={mainTheme.headerButtonColor}
                  sx={{ width: 190, fontWeight: "bold", textTransform: "none" }}
                >
                  arXiv (VisualRAG)
                </Button>
                <Button
                  href="#"
                  variant="outlined"
                  size="large"
                  color={mainTheme.headerButtonColor}
                  sx={{ width: 190, fontWeight: "bold", textTransform: "none" }}
                >
                  arXiv SEEME (Soon)
                </Button>
                <Button
                  href="#"
                  variant="outlined"
                  size="large"
                  color={mainTheme.headerButtonColor}
                  sx={{ width: 160, fontWeight: "bold", textTransform: "none" }}
                >
                  Datasets (Soon)
                </Button>
                <Button
                  href="#"
                  variant="outlined"
                  size="large"
                  color={mainTheme.headerButtonColor}
                  sx={{ width: 160, fontWeight: "bold", textTransform: "none" }}
                >
                  Code (Soon)
                </Button>
              </Container>
            </Box>
          </Container>
        </Box>

        {/* ── ABSTRACT ───────────────────────────────────────────────────── */}
        <Box
          sx={{
            backgroundColor: mainTheme.firstColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Abstract />
        </Box>

        {/* ── DEMO ───────────────────────────────────────────────────────── */}
        <Box
          sx={{
            backgroundColor: mainTheme.secondColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Demo />
        </Box>

        {/* ── APPROACH ───────────────────────────────────────────────────── */}
        <Box
          sx={{
            backgroundColor: mainTheme.firstColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Approach />
        </Box>

        {/* ── PUBLICATIONS ───────────────────────────────────────────────── */}
        <Box
          sx={{
            backgroundColor: mainTheme.secondColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Container maxWidth="md" sx={{ my: 6 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
              {/* Label */}
              <Stack sx={{ minWidth: 160, flexShrink: 0 }}>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 600, textAlign: { xs: "left", md: "right" } }}
                >
                  PUBLICATIONS
                </Typography>
              </Stack>

              {/* List */}
              <Stack spacing={3} sx={{ flex: 1 }}>
                {publications.map((pub, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      borderLeft: "3px solid",
                      borderColor: blueGrey[300],
                      pl: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {pub.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      {pub.authors}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "italic", color: blueGrey[700] }}
                    >
                      {pub.venue}
                      {pub.arxiv && (
                        <>
                          {" · "}
                          <Link
                            href={pub.arxiv}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            arXiv
                          </Link>
                        </>
                      )}
                      {!pub.arxiv && " · arXiv (Soon)"}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* ── BIBTEX ─────────────────────────────────────────────────────── */}
        <Box
          sx={{
            backgroundColor: mainTheme.firstColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Container maxWidth="md" sx={{ my: 6 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
              {/* Label */}
              <Stack sx={{ minWidth: 160, flexShrink: 0 }}>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 600, textAlign: { xs: "left", md: "right" } }}
                >
                  BIBTEX
                </Typography>
              </Stack>

              {/* Cards — one per paper, same style as CPAM */}
              <Stack spacing={3} sx={{ flex: 1, maxWidth: "100%" }}>
                {bibtexEntries.map((entry, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      backgroundColor: "#ffffff",
                      border: "1px solid",
                      borderColor: blueGrey[200],
                      borderRadius: 2,
                      p: 2,
                      overflow: "hidden",
                    }}
                  >
                    {/* Paper label */}
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: blueGrey[800], mb: 1 }}
                    >
                      {entry.label}
                    </Typography>

                    {/* Code block */}
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        border: "1px solid",
                        borderColor: blueGrey[100],
                        borderRadius: 1,
                        p: 1.5,
                        overflowX: "auto",
                        fontSize: "0.78rem",
                        fontFamily: "monospace",
                        color: blueGrey[900],
                        m: 0,
                        whiteSpace: "pre",
                      }}
                    >
                      {entry.code}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* ── ACKNOWLEDGEMENT ────────────────────────────────────────────── */}
        <Box
          sx={{
            backgroundColor: blueGrey.A100,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Container maxWidth="md" sx={{ my: 4 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
              <Stack sx={{ minWidth: 160, flexShrink: 0 }}>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 600, textAlign: { xs: "left", md: "right" } }}
                >
                  ACKNOWLEDGMENT
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="body1" component="span" sx={{ textAlign: "left" }}>
                  This research is funded by Vietnam National Foundation for
                  Science and Technology Development (NAFOSTED) under Grant
                  Number 102.05-2023.31.
                  <br />
                  <br />
                  This page was built using the template adopted from the{" "}
                  <Link
                    href="https://objectdrop.github.io"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ObjectDrop
                  </Link>{" "}
                  project page.
                </Typography>
                <ImageAttributions open={open} onClose={handleCloseAtt} />
              </Stack>
            </Stack>
          </Container>
        </Box>
      </div>
    </React.Fragment>
  );
}